import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './use-toast';
import { 
  getUserWatchlist, 
  addToWatchlist as addToWL, 
  removeFromWatchlist as removeFromWL,
  checkInWatchlist 
} from '../services/supabaseService';

export const useWatchlist = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Vérifier si une vidéo est dans la watchlist
  const isInWatchlist = useCallback((videoId) => {
    return watchlist.some(item => item.video_id === videoId);
  }, [watchlist]);

  // Charger la watchlist
  const loadWatchlist = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const response = await getUserWatchlist(user.uid);

      if (response.success) {
        setWatchlist(response.data || []);
      } else {
        throw new Error(response.error || 'Erreur récupération watchlist');
      }
    } catch (error) {
      console.error('Erreur chargement watchlist:', error);
      setError(error.message);
      setWatchlist([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Ajouter une vidéo à la watchlist
  const addToWatchlist = useCallback(async (videoId, videoTitle = '') => {
    if (!user) {
      toast({
        title: "Connexion requise",
        description: "Vous devez être connecté pour ajouter des vidéos à votre watchlist",
        variant: "destructive"
      });
      return false;
    }

    try {
      const response = await addToWL(user.uid, videoId);

      if (response.success) {
        // Recharger la watchlist pour avoir les données à jour
        await loadWatchlist();
        
        toast({
          title: "Ajouté à la watchlist ! ❤️",
          description: videoTitle ? `"${videoTitle}" ajouté à votre watchlist` : "Vidéo ajoutée à votre watchlist"
        });
        return true;
      } else {
        throw new Error(response.error || 'Erreur ajout');
      }
    } catch (error) {
      console.error('Erreur ajout watchlist:', error);
      
      const errorMessage = error.message || 'Erreur inconnue';
      
      if (errorMessage.includes('duplicate') || errorMessage.includes('déjà')) {
        toast({
          title: "Déjà dans la watchlist",
          description: "Cette vidéo est déjà dans votre watchlist",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Erreur ajout",
          description: `Impossible d'ajouter à la watchlist: ${errorMessage}`,
          variant: "destructive"
        });
      }
      return false;
    }
  }, [user, toast, loadWatchlist]);

  // Retirer une vidéo de la watchlist
  const removeFromWatchlist = useCallback(async (videoId, videoTitle = '') => {
    if (!user) return false;

    try {
      const response = await removeFromWL(user.uid, videoId);

      if (response.success) {
        // Mettre à jour l'état local
        setWatchlist(prev => prev.filter(item => item.video_id !== videoId));
        
        toast({
          title: "Retiré de la watchlist",
          description: videoTitle ? `"${videoTitle}" retiré de votre watchlist` : "Vidéo retirée de votre watchlist"
        });
        return true;
      } else {
        throw new Error(response.error || 'Erreur suppression');
      }
    } catch (error) {
      console.error('Erreur suppression watchlist:', error);
      
      // Supprimer quand même de l'état local
      setWatchlist(prev => prev.filter(item => item.video_id !== videoId));
      toast({
        title: "Retiré",
        description: "Vidéo retirée de votre watchlist",
      });
      return true;
    }
  }, [user, toast]);

  // Toggle watchlist (ajouter ou retirer)
  const toggleWatchlist = useCallback(async (videoId, videoTitle = '') => {
    if (isInWatchlist(videoId)) {
      return await removeFromWatchlist(videoId, videoTitle);
    } else {
      return await addToWatchlist(videoId, videoTitle);
    }
  }, [isInWatchlist, addToWatchlist, removeFromWatchlist]);

  // Charger la watchlist au montage
  useEffect(() => {
    loadWatchlist();
  }, [loadWatchlist]);

  return {
    watchlist,
    loading,
    error,
    isInWatchlist,
    addToWatchlist,
    removeFromWatchlist,
    toggleWatchlist,
    loadWatchlist,
    watchlistCount: watchlist.length
  };
};
